import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Select,
  FormControl,
} from '@mui/material';
import {
  useMiseSellersCurrent,
  useMiseSellersNext,
  useMiseSellerGroupsCurrent,
  useMiseSellerGroupsNext,
  useMiseSellerSellerGroupMapCurrent,
  useMiseSellerSellerGroupMapNext,
  useUpdateSellerSellerGroupMapNext,
} from '@/lib/query/hooks/useMiseSellers';
import { useMiseSellerGroups, useMiseSellers } from '@/lib/query/hooks/useMiseAccounts';
import {
  buildSellerGroupPath,
  describeLevelExamples,
  pickGroupAtLevel,
  indentSellerGroupLabel,
  sortSellerGroupsByTree,
} from '@/lib/mise/sellerGroupTree';
import type { MiseSellerGroupNode, MiseSellerSellerGroupMap } from '@/lib/types/mise/seller';
import { SellerGroupPathLabel } from './SellerGroupPathLabel';
import { useSellerGroupMapSort } from './useSellerGroupMapSort';

const COPY_SOURCE_CURRENT = 'current';

/** 引き継ぎ元。集計済みのマッピングか、階層ツリーの第N階層（1始まり） */
type CopySource = typeof COPY_SOURCE_CURRENT | number;

interface SellerSellerGroupMapNextEditDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
}

export function SellerSellerGroupMapNextEditDialog({
  open,
  onClose,
  accountId,
}: SellerSellerGroupMapNextEditDialogProps) {
  // seller_id -> seller_group_id のマッピング状態
  const [mappingState, setMappingState] = useState<Map<string, string>>(new Map());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [copySource, setCopySource] = useState<CopySource>(COPY_SOURCE_CURRENT);
  // 選んだ階層まで届かず最下層で引き継いだ店舗 → 実際に入れた階層。粗い区分が入ったことに気づけるよう注記を出す
  const [fallbackLevelBySeller, setFallbackLevelBySeller] = useState<Map<string, number>>(
    new Map(),
  );
  // 開くたびに一度だけ初期化するためのフラグ（開いている間の再フェッチによる入力リセットを防ぐ）
  const hasInitializedRef = useRef(false);

  // データ取得
  const { data: sellersCurrent } = useMiseSellersCurrent(accountId);
  const { data: sellersNext } = useMiseSellersNext(accountId);
  const { data: sellerGroupsCurrent } = useMiseSellerGroupsCurrent(accountId);
  const { data: sellerGroupsNext } = useMiseSellerGroupsNext(accountId);
  // 階層・並び順(sort_order)を持つ全店舗グループ（snapshot_hot 由来）。プルダウンの並び順と階層表示に使う。
  const { data: sellerGroupNodes, isLoading: sellerGroupNodesLoading } =
    useMiseSellerGroups(accountId);
  const { data: currentMappings, isLoading: mappingsLoading } =
    useMiseSellerSellerGroupMapNext(accountId);
  const { data: currentAppliedMappings, isLoading: currentAppliedMappingsLoading } =
    useMiseSellerSellerGroupMapCurrent(accountId);
  // 店舗が階層ツリー上で直接ぶら下がる店舗グループ（snapshot_hot 由来）。所属の表示に使う。
  const { data: sellerNodes, isLoading: sellerNodesLoading } = useMiseSellers(accountId);

  const updateMutation = useUpdateSellerSellerGroupMapNext(accountId);

  // 使用可能な店舗を判定
  const availableSellers = useMemo(
    () => (sellersNext && sellersNext.length > 0 ? sellersNext : (sellersCurrent ?? [])),
    [sellersNext, sellersCurrent],
  );

  // 使用可能な店舗グループを判定
  const availableSellerGroups = useMemo(
    () =>
      sellerGroupsNext && sellerGroupsNext.length > 0
        ? sellerGroupsNext
        : (sellerGroupsCurrent ?? []),
    [sellerGroupsNext, sellerGroupsCurrent],
  );

  // プルダウン表示用に、店舗グループを階層ツリー順（sort_order 昇順）へ並べ替え、各項目の階層深さ(depth)を付与する。
  // mise.seller_groups_* は name しか持たないため、snapshot_hot 由来の階層ツリーから順序・深さを引く。
  // これにより店舗グループ選択ダイアログと並び順・階層を揃え、割り当て時に階層構造を確認できる。
  const sellerGroupOptions = useMemo(
    () => sortSellerGroupsByTree(availableSellerGroups, sellerGroupNodes ?? []),
    [availableSellerGroups, sellerGroupNodes],
  );

  // 集計済み（current）のマッピング。「集計済みと同じ」で引き継ぐときに使う
  const currentGroupIdBySeller = useMemo(
    () => new Map((currentAppliedMappings ?? []).map((m) => [m.seller_id, m.seller_group_id])),
    [currentAppliedMappings],
  );

  // mise の map は比較対象設定用のフラットな 1:1 で階層を持たないため、所属の表示は階層ツリーから引く
  const groupPathBySeller = useMemo(() => {
    const paths = new Map<string, MiseSellerGroupNode[]>();
    for (const seller of sellerNodes ?? []) {
      paths.set(seller.id, buildSellerGroupPath(seller.parent_id, sellerGroupNodes ?? []));
    }
    return paths;
  }, [sellerNodes, sellerGroupNodes]);

  const availableSellerGroupIds = useMemo(
    () => new Set(availableSellerGroups.map((g) => g.id)),
    [availableSellerGroups],
  );

  // データにある最も深い階層の分だけ「第N階層」の選択肢を出す
  const { sort, sortedSellers, handleSortChange, resetSort } = useSellerGroupMapSort(
    availableSellers,
    sellerNodes,
    groupPathBySeller,
  );

  const levelOptions = useMemo(() => {
    const paths = Array.from(groupPathBySeller.values());
    const maxDepth = Math.max(0, ...paths.map((path) => path.length));
    return Array.from({ length: maxDepth }, (_, i) => ({
      level: i + 1,
      examples: describeLevelExamples(paths, i + 1),
    }));
  }, [groupPathBySeller]);

  // 店舗グループの並び順データ(sellerGroupNodes)の取得完了も待つ。
  // 取得中にプルダウンを開くと、データ到着でメニュー項目の並び順が変化し、その再描画でプルダウンが閉じてしまうため。
  const isLoading =
    mappingsLoading ||
    sellerGroupNodesLoading ||
    currentAppliedMappingsLoading ||
    sellerNodesLoading;

  // 初期値の設定
  // 開いている間の再フェッチで入力中のマッピングがリセットされないよう、開くたびに一度だけ初期化する。
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) return;
    if (currentMappings === undefined) return;
    const initialMap = new Map<string, string>();
    for (const mapping of currentMappings) {
      initialMap.set(mapping.seller_id, mapping.seller_group_id);
    }
    setMappingState(initialMap);
    setFallbackLevelBySeller(new Map());
    resetSort();
    hasInitializedRef.current = true;
  }, [open, currentMappings]);

  const handleClose = () => {
    if (!updateMutation.isPending) {
      setConfirmDialogOpen(false);
      onClose();
    }
  };

  const handleGroupChange = (sellerId: string, sellerGroupId: string) => {
    setFallbackLevelBySeller((prev) => {
      if (!prev.has(sellerId)) return prev;
      const next = new Map(prev);
      next.delete(sellerId);
      return next;
    });
    setMappingState((prev) => {
      const newMap = new Map(prev);
      if (sellerGroupId === '') {
        newMap.delete(sellerId);
      } else {
        newMap.set(sellerId, sellerGroupId);
      }
      return newMap;
    });
  };

  // 一括割り当て
  const handleBulkAssign = (sellerGroupId: string) => {
    setFallbackLevelBySeller(new Map());
    setMappingState(() => {
      const newMap = new Map<string, string>();
      for (const seller of availableSellers) {
        newMap.set(seller.id, sellerGroupId);
      }
      return newMap;
    });
  };

  // 集計前の店舗グループに無いグループは、さらに上の階層までさかのぼると
  // 全店のような粗い区分が気づかないうちに入るため未設定のまま残す
  const resolveCopyTarget = (sellerId: string): { groupId: string; level?: number } | undefined => {
    if (copySource === COPY_SOURCE_CURRENT) {
      const groupId = currentGroupIdBySeller.get(sellerId);
      return groupId !== undefined && availableSellerGroupIds.has(groupId)
        ? { groupId }
        : undefined;
    }
    const picked = pickGroupAtLevel(groupPathBySeller.get(sellerId) ?? [], copySource);
    return picked && availableSellerGroupIds.has(picked.node.id)
      ? { groupId: picked.node.id, level: picked.level }
      : undefined;
  };

  const handleCopy = () => {
    const newMap = new Map<string, string>();
    const fallbackLevels = new Map<string, number>();
    for (const seller of availableSellers) {
      const target = resolveCopyTarget(seller.id);
      if (!target) continue;
      newMap.set(seller.id, target.groupId);
      if (target.level !== undefined && target.level !== copySource) {
        fallbackLevels.set(seller.id, target.level);
      }
    }
    setMappingState(newMap);
    setFallbackLevelBySeller(fallbackLevels);
  };

  const hasCopyable = availableSellers.some((s) => resolveCopyTarget(s.id) !== undefined);

  const unassignedCount = availableSellers.filter((s) => !mappingState.has(s.id)).length;
  const allAssigned = unassignedCount === 0 && availableSellers.length > 0;

  const handleSave = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);

    const mappings: MiseSellerSellerGroupMap[] = Array.from(mappingState.entries()).map(
      ([seller_id, seller_group_id]) => ({ seller_id, seller_group_id }),
    );

    try {
      await updateMutation.mutateAsync({ mappings });
      onClose();
    } catch (error) {
      console.error('Failed to update seller-seller group map next:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>集計前の店舗-店舗グループマッピングを編集</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
              </Box>
            ) : availableSellers.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                店舗が登録されていません
              </Alert>
            ) : availableSellerGroups.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                店舗グループが登録されていません
              </Alert>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    未割り当て: {unassignedCount}件 / 全{availableSellers.length}件
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      columnGap: 3,
                      rowGap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        現在の所属を設定:
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          native
                          value={String(copySource)}
                          onChange={(e) =>
                            setCopySource(
                              e.target.value === COPY_SOURCE_CURRENT
                                ? COPY_SOURCE_CURRENT
                                : Number(e.target.value),
                            )
                          }
                        >
                          <option value={COPY_SOURCE_CURRENT}>集計済みと同じ</option>
                          {levelOptions.map(({ level, examples }) => (
                            <option key={level} value={level}>
                              第{level}階層{examples ? `（${examples}）` : ''}
                              {level > 1 ? '／ない場合は最下層' : ''}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCopy}
                        disabled={!hasCopyable}
                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        設定
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        一括割り当て:
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          native
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              handleBulkAssign(e.target.value);
                            }
                          }}
                        >
                          <option value="">店舗グループを選択</option>
                          {sellerGroupOptions.map(({ group, depth }) => (
                            <option key={group.id} value={group.id}>
                              {indentSellerGroupLabel(depth, group.name || group.id)}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 500, overflow: 'auto' }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sort.key === 'name'}
                            direction={sort.key === 'name' ? sort.direction : 'asc'}
                            onClick={() => handleSortChange('name')}
                          >
                            店舗名
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sort.key === 'group'}
                            direction={sort.key === 'group' ? sort.direction : 'asc'}
                            onClick={() => handleSortChange('group')}
                          >
                            現在の店舗グループ
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>店舗グループ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedSellers.map((seller) => (
                        <TableRow key={seller.id} hover>
                          <TableCell>
                            <Typography variant="body2">{seller.name || seller.id}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {seller.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <SellerGroupPathLabel path={groupPathBySeller.get(seller.id) ?? []} />
                          </TableCell>
                          <TableCell>
                            <FormControl
                              size="small"
                              fullWidth
                              error={!mappingState.has(seller.id)}
                            >
                              <Select
                                native
                                value={mappingState.get(seller.id) ?? ''}
                                onChange={(e) => handleGroupChange(seller.id, e.target.value)}
                              >
                                <option value="">未設定</option>
                                {sellerGroupOptions.map(({ group, depth }) => (
                                  <option key={group.id} value={group.id}>
                                    {indentSellerGroupLabel(depth, group.name || group.id)}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                            {fallbackLevelBySeller.has(seller.id) && (
                              <Typography variant="caption" color="warning.main">
                                {`第${fallbackLevelBySeller.get(seller.id)}階層までのため、最下層で設定しました`}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {updateMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : '店舗-店舗グループマッピングの更新に失敗しました'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateMutation.isPending || !allAssigned || isLoading}
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400,
          },
        }}
      >
        <DialogTitle>店舗-店舗グループマッピングを更新しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            {availableSellers.length}件の店舗のマッピングが更新されます。
            この操作により、既存のマッピングは上書きされます。よろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
